
import { defineAsyncComponent } from "vue";
import { Options, Vue } from "vue-class-component";
import Tooltip from "@/app/ui/components/tooltip/index.vue";
import { AccountController } from "@/app/ui/controllers/AccountController";
import debounce from "lodash/debounce";
import dateToDateString from "@/app/infrastructures/misc/common-library/DateToDateString";
import dateToTime from "@/app/infrastructures/misc/common-library/DateToTime";
import formatPrice from "@/app/infrastructures/misc/common-library/v2/FormatPrice";
import { BalanceController } from "@/app/ui/controllers/BalanceController";
import { TransactionListData } from "@/domain/entities/Balance";
import { BalanceTransactionControllers } from "@/app/ui/controllers/BalanceTransactionControllers";
import {
  BalanceTransactionType,
  TransactionTypeGober
} from "@/domain/entities/BalanceTransaction";
import DownloadRequest from "@/app/ui/components/widget-download-request/index.vue";
import { FlagsPermissionSaldo } from "@/feature-flags/flags-permission-saldo";
import { FlagsPermissionTopUpManual } from "@/feature-flags/flags-permission-topup-manual";
import levelingPartner from "@/app/infrastructures/misc/common-library/LevelingPartner";
import levelTooltip from "@/app/infrastructures/misc/common-library/LevelTooltip";
import formatDate from "@/app/infrastructures/misc/common-library/v2/FormatDate";

const ModalWithdraw = defineAsyncComponent({
  loader: () => import("@/app/ui/views/balance/component/modal-withdraw.vue")
});
const ModalTopup = defineAsyncComponent({
  loader: () => import("@/app/ui/views/balance/component/modal-topup.vue")
});
const ModalFormTopupManual = defineAsyncComponent({
  loader: () =>
    import("@/app/ui/views/balance/component/modal-form-topup-manual.vue")
});
const ModalDetailTopupManual = defineAsyncComponent({
  loader: () =>
    import("@/app/ui/views/balance/component/modal-detail-topup-manual.vue")
});
const ModalListTopupHistory = defineAsyncComponent({
  loader: () =>
    import("@/app/ui/views/balance/component/modal-list-topup-history.vue")
});
const ModalDownloadMutation = defineAsyncComponent({
  loader: () =>
    import("@/app/ui/views/balance/component/modal-download-mutation.vue")
});

@Options({
  components: {
    Tooltip,
    ModalTopup,
    ModalFormTopupManual,
    ModalListTopupHistory,
    ModalDetailTopupManual,
    ModalWithdraw,
    ModalDownloadMutation,
    DownloadRequest
  },
  computed: {
    isWithdrawDetail() {
      return (
        FlagsPermissionSaldo.permission_saldo_withdraw_view_detail.isEnabled() &&
        FlagsPermissionSaldo.permission_saldo_withdraw_request_detail.isEnabled()
      );
    },
    isEnableTopUp() {
      return (
        FlagsPermissionTopUpManual.permission_topup_manual_enable.isEnabled() &&
        !this.accountIsForeign
      );
    }
  }
})
export default class TransactionList extends Vue {
  async created() {
    BalanceController.initTransactionList();
    this.fullOptions = await BalanceTransactionControllers.getBalanceTransactionType(
      "v2"
    );
  }

  get countryCode() {
    const code = AccountController.accountData?.countryCode;
    return code?.toUpperCase() ?? "ID";
  }

  get balanceData() {
    return BalanceController.balanceData;
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  get balance() {
    const currency = this.countryCode === "MY" ? "MYR" : "IDR";

    return formatPrice(
      BalanceController.balanceData.walletBalance,
      "",
      currency
    );
  }

  get accountCurrency(): "IDR" | "MYR" {
    const d = AccountController.accountData;
    return (
      (d.accountCurrency as "IDR" | "MYR") ||
      (this.countryCode === "MY" ? "MYR" : "IDR")
    );
  }

  get balanceDeposit() {
    const currency = this.countryCode === "MY" ? "MYR" : "IDR";

    return formatPrice(
      AccountController.accountData.account_type_detail.partnerPosSaldoDeposit,
      "",
      currency
    );
  }

  get clientCurrency(): "IDR" | "MYR" {
    return (AccountController.accountData.countryCurrency || "IDR") as
      | "IDR"
      | "MYR";
  }

  get accountIsForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  get showBalanceWarning() {
    return (
      BalanceController.balanceData.walletBalance <=
      this.dataProfile.limit_rule_min_balance +
        this.dataProfile.limit_rule_limit_border
    );
  }

  get balanceWarning() {
    return (
      BalanceController.balanceData.walletBalance <=
        this.dataProfile.limit_rule_min_balance +
          this.dataProfile.limit_rule_limit_border &&
      BalanceController.balanceData.walletBalance >=
        this.dataProfile.limit_rule_min_balance
    );
  }

  get balanceWarningMessage() {
    const key = this.balanceWarning
      ? "balance.warningNearLimit"
      : BalanceController.balanceData.walletBalance <
        this.dataProfile.limit_rule_min_balance
      ? "balance.warningBelowLimit"
      : "balance.warningAtLimit";
    return this.$t(key);
  }

  //tooltip
  isOpenTooltip = false;
  openTooltip = debounce((val: boolean) => {
    this.isOpenTooltip = val;
  }, 50);

  // search
  get search() {
    return BalanceController.search;
  }
  onSearch(value: string) {
    BalanceController.setSearch(value);
    BalanceController.setFirstPage();
    this.fetchTransactionList();
  }

  clearSearch() {
    BalanceController.setSearch("");
    BalanceController.setFirstPage();
    this.fetchTransactionList();
  }

  // select
  fullOptions = new TransactionTypeGober();
  isOpenSelect = false;
  get options() {
    if (this.dataProfile.account_type === "client") {
      return [
        new BalanceTransactionType({
          name: this.$t("balance.filterAll"),
          value: ""
        }),
        ...this.fullOptions.balanceClient
      ];
    }
    return [
      new BalanceTransactionType({
        name: "Semua",
        value: ""
      }),
      ...this.fullOptions.balancePos
    ];
  }
  get valueSelect() {
    return BalanceController.filter;
  }
  onSelect(value: any) {
    if (value.name === this.valueSelect.name) {
      BalanceController.setFilter({ name: "", value: "" });
    } else {
      BalanceController.setFilter(value);
    }
    BalanceController.setFirstPage();
    this.fetchTransactionList();
  }

  // time range picker
  minDate: any = null;
  maxDate: any = null;
  tempStartDate: any = null;
  tempEndDate: any = null;
  isStartDate = true;
  setDay(day: any) {
    if (this.isStartDate) {
      this.tempStartDate = this.startDate;
      this.tempEndDate = this.endDate;
      BalanceController.setStartDate(null);
      BalanceController.setEndDate(null);
      this.minDate = new Date(
        new Date(day.id).setDate(new Date(day.id).getDate() - 89)
      );
      this.maxDate = new Date(
        new Date(day.id).setDate(new Date(day.id).getDate() + 89)
      );
      this.isStartDate = false;
    } else {
      this.endDate && (this.isStartDate = true);
    }
  }

  cancelRangePick() {
    if (!this.isStartDate) {
      this.minDate = null;
      this.maxDate = null;
      this.isStartDate = true;
      BalanceController.setStartDate(this.tempStartDate);
      BalanceController.setEndDate(this.tempEndDate);
      this.tempStartDate = null;
      this.tempEndDate = null;
    }
  }

  get startDate() {
    return BalanceController.startDate;
  }

  get endDate() {
    return BalanceController.endDate;
  }

  setDateRange(value: Array<any>) {
    this.minDate = null;
    this.maxDate = null;
    BalanceController.setStartDate(value[0]);
    BalanceController.setEndDate(value[1]);
    value[0] && value[1] && BalanceController.setFirstPage();
    value[0] && value[1] && this.fetchTransactionList();
  }

  // table
  get transactionList() {
    return BalanceController.transactionList.data;
  }

  get pagination() {
    return BalanceController.transactionList.pagination;
  }

  fetchTransactionList() {
    BalanceController.fetchTransactionList("v2");
  }

  get isError() {
    return BalanceController.isError;
  }

  get errorCause() {
    return BalanceController.errorCause;
  }

  get isLoading() {
    return BalanceController.isLoading;
  }

  get columns() {
    return [
      {
        name: this.$t("balance.tableTitle.genesisTransactionId"),
        styleHead: "text-black-lp-300 w-56 text-left whitespace-no-wrap",
        render: (item: TransactionListData) => {
          return `<div class="text-black-lp-300 capitalize text-left break-all">${item.pgTrxNo}</div>`;
        }
      },
      {
        name: this.$t("balance.tableTitle.type"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: TransactionListData) => {
          return `<div class="text-black-lp-300 capitalize text-left break-words">${item.pgTrxType.replace(
            /_/g,
            " "
          )}</div>`;
        }
      },
      {
        name: this.$t("balance.tableTitle.sttNo"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: TransactionListData) => {
          return `<div class="text-black-lp-300 capitalize text-left">${
            item.pgTrxSttNo ? item.pgTrxSttNo : "-"
          }</div>`;
        }
      },
      {
        name: this.$t("balance.tableTitle.activity"),
        styleHead: "text-black-lp-300 w-20 text-left whitespace-no-wrap",
        render: (item: TransactionListData) => {
          return `<div class="text-black-lp-300 capitalize overflow-ellipsis text-left">${item.pgTrxActivity}</div>`;
        }
      },
      {
        name: this.$t("balance.tableTitle.amount"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: TransactionListData) => {
          const isCredit = item.pgTrxActivity === "credit";
          const sign = isCredit ? "+" : "-";
          const colorClass = isCredit ? "text-green-lp-100" : "text-red-lp-500";
          const primaryAmount = `${sign}${formatPrice(
            item.pgTrxAmount,
            "",
            this.clientCurrency
          )}`;

          const secondaryAmount =
            item.destinationAmount && item.destinationCurrency
              ? `<div class="text-11px text-gray-lp-600 font-normal">(${formatPrice(
                  item.destinationAmount,
                  "",
                  (item.destinationCurrency || "IDR") as "IDR" | "MYR"
                )})</div>`
              : "";

          return `<div class="capitalize flex flex-col">
            <div class="font-medium text-left ${colorClass}">${primaryAmount}</div>
            ${secondaryAmount}
          </div>`;
        },
      },
      {
        name: this.$t("balance.tableTitle.lastBalance"),
        styleHead: "text-black-lp-300 w-60 text-left whitespace-no-wrap",
        render: (item: TransactionListData) => {
          return `<div class="font-medium text-left">${formatPrice(
            item.pgTrxLastBalance,
            "",
            this.clientCurrency
          )}</div>`;
        }
      },
      {
        name: this.$t("balance.tableTitle.trancasationDate"),
        styleHead: "text-black-lp-300 w-40 text-left whitespace-no-wrap",
        render: (item: TransactionListData) => {
          return `<div class="text-black-lp-300 capitalize overflow-ellipsis text-left">${formatDate(
            item.pgTrxCreatedAt
          )}</div>`;
        }
      },
      {
        name: this.$t("balance.tableTitle.status"),
        styleHead: "text-black-lp-300 w-24 text-left whitespace-no-wrap",
        render: (item: TransactionListData) => {
          const trxStatusHandled = this.handleTrxStatus(item.pgTrxStatus);
          return `<div class="capitalize overflow-ellipsis text-left flex flex-row gap-x-1 font-medium
         ${this.handleClassTextColorTrxStatus(trxStatusHandled)}">
            ${this.handleImgTrx(trxStatusHandled)}
            ${trxStatusHandled}
          </div>`;
        }
      }
    ];
  }
  handleImgTrx(status: string) {
    if (status === "success") {
      return `<img class="w-3 max-w-none" src="${require("@/app/ui/assets/svg/status-success.svg")}" />`;
    }
    if (status === "failed") {
      return `<img class="w-3 max-w-none" src="${require("@/app/ui/assets/svg/status-failed.svg")}" />`;
    }
    return `<img class="w-3 max-w-none" src="${require("@/app/ui/assets/svg/status-request.svg")}" />`;
  }
  handleClassTextColorTrxStatus(status: string) {
    if (status === "success") {
      return "text-green-lp-100";
    }
    return status === "failed" ? "text-red-lp-750" : "text-yellow-lp-100";
  }
  handleTrxStatus(status: string) {
    status = status.toLowerCase();
    if (status === "approved") {
      return "success";
    }
    if (status === "rejected") {
      return "failed";
    }
    return status;
  }

  onModalDownload() {
    this.setModalDownloadMutation(true);
  }

  setModalDownloadMutation(boolean: boolean) {
    BalanceController.setModalDownloadMutation(boolean);
  }

  setModalSuccess(boolean: boolean) {
    BalanceController.setModalSuccess(boolean);
  }

  get isModalSuccessDownloadMutation() {
    return BalanceController.modalSuccess;
  }

  get isModalDownloadMutation() {
    return BalanceController.modalDownloadMutation;
  }

  openModalTopup() {
    return BalanceController.setOpenModalTopup(true);
  }

  closeModalTopup() {
    return BalanceController.setOpenModalTopup(false);
  }

  get isOpenModalTopup() {
    return BalanceController.isOpenModalTopup;
  }

  get isShowTransactionHistory() {
    return BalanceController.showTopupTransactionHistory;
  }

  get isShowTopupManualDetail() {
    return BalanceController.isShowRequestTopupManualDetail;
  }

  get isDisableWithdraw() {
    return (
      BalanceController.balanceData.walletBalance <
        this.dataProfile.limit_rule_min_balance ||
      this.dataProfile.account_type === "client"
    );
  }

  isOpenModalWithdraw = false;
  openModalWithdraw() {
    if (this.isDisableWithdraw) {
      return;
    }
    this.isOpenModalWithdraw = true;
  }

  closeModalWithdraw() {
    this.isOpenModalWithdraw = false;
  }

  get isReportDownload() {
    return true;
  }

  get level() {
    return levelingPartner(
      AccountController.accountData.account_type_detail.partnerPosTieringLevel
    );
  }

  get levelTooltip() {
    return levelTooltip(
      AccountController.accountData.account_type_detail.partnerPosTieringLevel
    )?.message;
  }

  isopenTooltipBalanceDeposit = false;
  openTooltipBalanceDeposit = debounce((val: boolean) => {
    this.isopenTooltipBalanceDeposit = val;
  }, 50);

  isopenTooltipBalanceAvailable = false;
  openTooltipBalanceAvailable = debounce((val: boolean) => {
    this.isopenTooltipBalanceAvailable = val;
  }, 50);

  isopenTooltipLevel = false;
  openTooltipLevel = debounce((val: boolean) => {
    this.isopenTooltipLevel = val;
  }, 50);
}
